import React from "react";
import { Instagram, MapPin, Clock, Car, Navigation, ArrowUpRight } from "lucide-react";
import { CONTACT } from "../../mock";
import { WhatsApp, TikTok } from "../Social";
import { Reveal, Magnetic } from "../../lib/motion";

const CHANNELS = [
  {
    key: "whatsapp",
    icon: <WhatsApp size={22} />,
    label: "WhatsApp",
    value: "Message us instantly",
    href: CONTACT.whatsapp,
    testid: "contact-whatsapp",
  },
  {
    key: "instagram",
    icon: <Instagram size={22} />,
    label: "Instagram",
    value: "@thejacksonhousergv",
    href: CONTACT.socials.instagram,
    testid: "contact-instagram",
  },
  {
    key: "tiktok",
    icon: <TikTok size={20} />,
    label: "TikTok",
    value: "@thejacksonhousergv956",
    href: CONTACT.socials.tiktok,
    testid: "contact-tiktok",
  },
];

export default function ContactSection() {
  return (
    <section id="contact" className="bg-[#F7F2E9] py-28" data-testid="contact-section">
      <div className="max-w-[1100px] mx-auto px-6">
        <div className="text-center mb-14">
          <Reveal>
            <span className="inline-block font-body uppercase tracking-[0.35em] text-xs text-[var(--moss-deep)] mb-5">
              Say Hello
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-black text-[#0A3B2E] text-5xl md:text-7xl leading-none">
              Connect <span className="italic gradient-text font-light">With Us</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="text-[#16241E]/60 max-w-xl mx-auto mt-6 text-lg font-light">
              Reach out for availability, overnight stays &amp; event pricing — we'd love to help
              plan your paradise getaway.
            </p>
          </Reveal>
        </div>

        {/* Contact channels */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {CHANNELS.map((c, i) => (
            <Reveal key={c.key} delay={0.08 * i}>
              <a
                href={c.href}
                target="_blank"
                rel="noreferrer"
                data-cursor="hover"
                data-testid={c.testid}
                className="group flex items-start gap-4 rounded-[22px] bg-white/60 border border-white/70 p-6 h-full shadow-[0_15px_40px_rgba(10,59,46,0.07)] hover:-translate-y-1 transition-transform"
              >
                <span className="w-12 h-12 rounded-full sunset-fill grid place-items-center text-white shrink-0">
                  {c.icon}
                </span>
                <div>
                  <p className="font-body uppercase tracking-widest text-[10px] text-[#16241E]/45">{c.label}</p>
                  <p className="font-body font-semibold text-[#0A3B2E]">{c.value}</p>
                  <span className="inline-flex items-center gap-1 mt-1 text-sm text-[var(--moss-deep)] opacity-0 group-hover:opacity-100 transition-opacity">
                    Open <ArrowUpRight size={13} />
                  </span>
                </div>
              </a>
            </Reveal>
          ))}
        </div>

        {/* Location + directions */}
        <Reveal delay={0.15}>
          <div className="mt-8 rounded-[26px] bg-white/50 backdrop-blur-sm border border-white/60 p-8 shadow-[0_20px_50px_rgba(10,59,46,0.08)] flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-full bg-[#0A3B2E]/5 grid place-items-center text-[var(--moss-deep)] shrink-0">
                <MapPin size={22} />
              </span>
              <div>
                <p className="font-body uppercase tracking-widest text-[10px] text-[#16241E]/45">Location</p>
                <p className="font-display font-bold text-xl text-[#0A3B2E]">{CONTACT.address}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Magnetic strength={0.3}>
                <a
                  href={CONTACT.maps.apple}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-apple-maps"
                  className="btn-shine inline-flex items-center gap-2 rounded-full sunset-fill text-white font-body font-bold px-6 py-3 text-sm"
                >
                  <Navigation size={16} /> Get Directions
                </a>
              </Magnetic>
              <a
                href={CONTACT.maps.google}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-google-maps"
                className="inline-flex items-center gap-2 rounded-full border border-[#0A3B2E]/20 text-[#0A3B2E] hover:border-[var(--moss)] hover:text-[var(--moss-deep)] px-5 py-3 text-sm transition-colors"
              >
                Google Maps
              </a>
            </div>
          </div>
        </Reveal>

        {/* Airbnb guest info */}
        <Reveal delay={0.2}>
          <div className="mt-8 text-center">
            <p className="font-display font-bold text-xl text-[#0A3B2E] mb-4">For Airbnb guests</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-[#16241E]/70">
              <p className="flex items-center gap-2">
                <Clock size={18} className="text-[var(--moss)]" /> Check-in {CONTACT.checkIn} &middot; Check-out {CONTACT.checkOut}
              </p>
              {CONTACT.notes.map((n, i) => (
                <p key={i} className="flex items-center gap-2">
                  <Car size={18} className="text-[var(--moss)]" /> {n}
                </p>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
